import client from './client';
import { _createAlert } from './apiUtils';

/**
 * Misc Task Trending report — 8 weeks of approved misc-claim ("red") dollars,
 * rolled up by supervisor. NPM only; the API refuses with 404 on other tenants.
 */

export type MiscTaskTrendFormat = 'pdf' | 'xlsx';

export interface MiscTaskTrendSupervisor {
    employeeID: number;
    altEmployeeID: string;
    /** 'Last, First' — already formatted by the API. */
    name: string;
    /** Active direct reports, so two same-named people are still tellable apart. */
    directReports: number;
}

export interface MiscTaskTrendSupervisorsResponse {
    /** Preselect this — the caller themselves when they supervise anyone. */
    defaultEmployeeID: number | null;
    options: MiscTaskTrendSupervisor[];
}

/** The magic bytes each format must start with. */
const MAGIC: Record<MiscTaskTrendFormat, { bytes: number[]; label: string }> = {
    // '%PDF'
    pdf: { bytes: [0x25, 0x50, 0x44, 0x46], label: 'PDF' },
    // 'PK\x03\x04' — xlsx is a zip
    xlsx: { bytes: [0x50, 0x4b, 0x03, 0x04], label: 'Excel workbook' },
};

const CONTENT_TYPES: Record<MiscTaskTrendFormat, string> = {
    pdf: 'application/pdf',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/**
 * Pulls a readable message out of an arraybuffer error body.
 *
 * With `responseType: 'arraybuffer'` axios does NOT parse a JSON error body, so
 * `error.response.data` is an ArrayBuffer and the useful message —
 * "Not authorized", "Pay period 1099 was not found" — is invisible unless it is
 * decoded here. Without this every 403 and 404 surfaces as "Request failed with
 * status code 403", which tells the user nothing about what to do.
 */
const messageFromArrayBufferError = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
        try {
            const text = new TextDecoder().decode(data as ArrayBuffer);
            const parsed = JSON.parse(text);
            if (parsed?.message) return parsed.message;
        } catch {
            /* not JSON — fall through */
        }
    }
    return error?.response?.data?.message ?? error?.message ?? fallback;
};

/**
 * The supervisors the caller may run this report for — themselves plus everyone
 * beneath them, or the whole company for an org-wide role.
 */
export const getMiscTaskTrendSupervisors =
    async (): Promise<MiscTaskTrendSupervisorsResponse | null> => {
        try {
            const response = await client.get('/admin/miscTaskTrending/supervisors');
            return response.data ?? null;
        } catch (error: any) {
            console.error('Error loading misc task trending supervisors:', error);
            _createAlert(
                'Could Not Load Supervisors',
                error?.response?.data?.message ?? error?.message ?? 'Failed to load supervisors.'
            );
            return null;
        }
    };

/**
 * Builds and downloads the report.
 *
 * The magic-byte check is not paranoia: a JSON error body saved as `.pdf` gives
 * the user a file that will not open and no idea why. Same guard the audit
 * reports use (`downloadReportPdf` in api/audit.ts), extended to cover xlsx.
 */
export const downloadMiscTaskTrendReport = async (
    payPeriodID: number,
    supervisorEmployeeID: number,
    format: MiscTaskTrendFormat,
    fileName: string
): Promise<boolean> => {
    try {
        const response = await client.post(
            '/admin/miscTaskTrending/report',
            { payPeriodID, supervisorEmployeeID, format },
            { responseType: 'arraybuffer' }
        );

        const bytes = new Uint8Array(response.data);
        const { bytes: expected, label } = MAGIC[format];
        if (!expected.every((byte, index) => bytes[index] === byte)) {
            console.error(
                'Expected a ' + label + ', got:',
                new TextDecoder().decode(bytes).slice(0, 300)
            );
            _createAlert(
                'Report Failed',
                `The server did not return a valid ${label}.`
            );
            return false;
        }

        // `fileName` is supplied by the caller, NOT read off Content-Disposition.
        // That header is not CORS-safelisted, so on a cross-origin XHR — which
        // this always is, the app and the API being on different origins in every
        // environment — the browser hides it from JS and every download lands as
        // the same generic name. See miscTaskTrendFileName in
        // Pages/v2/miscTaskTrendHelpers.ts.
        const blob = new Blob([response.data], { type: CONTENT_TYPES[format] });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);

        return true;
    } catch (error: any) {
        console.error('Error generating the misc task trending report:', error);
        _createAlert(
            'Report Failed',
            messageFromArrayBufferError(error, 'Failed to generate the report.')
        );
        return false;
    }
};
